import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter,
    HostBinding, HostListener, Input, OnDestroy, Output, ViewChild, forwardRef,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { DxcSelectOption } from '../dxc.types';
import { DxcPanelAnchor, DxcPanelWidth } from '../panel/dxc-panel-anchor';

let nextId = 0;

/**
 * Desplegable de la aplicación, en reemplazo de los `<select>` nativos.
 *
 * El popup nativo lo dibuja el navegador fuera de la página y no respeta la
 * escala del viewport — en móvil sale diminuto y descolocado —, y además no
 * admite buscador, banderas ni multiselección. Es la decisión D4 de la regla.
 *
 * Dos cosas cambian respecto a la primera versión, las dos por defectos vistos
 * en la vista de borrador:
 *
 * 1. **El disparador es el campo de búsqueda.** Antes era un `<button>` y el
 *    filtro vivía en un `<input>` dentro del panel: el usuario pulsaba un sitio
 *    y escribía en otro. Ahora se escribe donde se hizo clic.
 *
 * 2. **El panel se posiciona con `position: fixed`.** Con `absolute` lo
 *    recortaba cualquier ancestro con `overflow: hidden` — la tarjeta de
 *    contenedor y el propio `.dxc-input-group` lo hacían, y solo se veían las
 *    dos primeras opciones.
 *
 * Funciona con formularios reactivos vía CVA y también con `[(value)]`.
 */
@Component({
    standalone: true,
    selector: 'dxc-select',
    templateUrl: './dxc-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DxcSelectComponent), multi: true }],
})
export class DxcSelectComponent implements ControlValueAccessor, OnDestroy {

    @Input() options: DxcSelectOption[] = [];
    @Input() placeholder = 'Selecciona una opción';
    /** Id del campo, para asociarlo al `<label for>`. */
    @Input() selectId = '';
    /**
     * Pinta el campo en rojo. Quien lo pasa decide *cuándo*: un campo requerido
     * no debe salir en rojo antes de que el usuario lo toque o intente guardar.
     */
    @Input() invalid = false;
    @Input() ariaLabel = '';
    /**
     * Se conserva por compatibilidad: ahora se puede escribir siempre, porque el
     * campo y el buscador son el mismo control. Solo decide si el texto tecleado
     * filtra la lista o únicamente mueve el cursor por ella.
     */
    @Input() searchable = false;
    @Input() searchPlaceholder = 'Buscar…';
    @Input() emptyLabel = 'Sin resultados';
    @Input() clearable = true;
    /** Sin efecto desde que el panel se ancla al campo; se mantiene por API. */
    @Input() align: 'left' | 'right' = 'left';

    /**
     * Ancho del panel.
     *
     * `field` — pegado al ancho del campo. Es lo correcto cuando las etiquetas
     * son cortas: el panel se lee como una extensión del control.
     *
     * `auto` — el panel crece hasta lo que pida la etiqueta más larga, sin bajar
     * del ancho del campo ni salirse de la pantalla. Para catálogos cuyo texto no
     * cabe en la celda: en el de clases de material peligroso, 22 de 36 opciones
     * salían recortadas dentro de un campo de 174px, y «3.1 FLAMMABLE LIQUID» y
     * «3.2 FLAMMABLE LIQUID» se leían idénticas.
     */
    @Input() panelWidth: DxcPanelWidth = 'field';
    /** Tope del ancho automático. */
    @Input() panelMaxWidth = 520;

    /**
     * Cuántas opciones se dibujan a la vez.
     *
     * El panel dibujaba **todas**, siempre. Con el catálogo de ubicaciones
     * guardadas eso son 987 `<button>` —2 961 nodos, 17 778 px de lista— creados
     * de golpe al enfocar el campo y revisados en cada ciclo de detección: la
     * pestaña se queda sin responder. Y no se ven: el panel mide 280 px, así que
     * de las 987 el usuario tiene delante ocho.
     *
     * El resto no se oculta en silencio — el pie dice cuántas quedan fuera y que
     * se siga escribiendo, que es además cómo se usa un campo con buscador.
     */
    @Input() maxRendered = 100;

    @HostBinding('class.dxc-combo') readonly hostClass = true;
    @HostBinding('class.is-open') get isOpen() { return this.open; }

    @Input() set value(value: string) { this.#value = value ?? ''; }
    get value() { return this.#value; }
    @Output() valueChange = new EventEmitter<string>();

    @ViewChild('field') fieldRef?: ElementRef<HTMLInputElement>;

    readonly panelId = `dxc-combo-${nextId++}`;

    #value = '';
    /** También por entrada: un envoltorio que no es CVA —el selector de país— lo pasa así. */
    @Input() disabled = false;
    open = false;
    activeIndex = -1;
    query = '';
    /** El usuario está escribiendo: el campo muestra `query`, no la etiqueta. */
    typing = false;
    /** El panel es `fixed`; el ancla le calcula las coordenadas. */
    private anchor!: DxcPanelAnchor;
    get panelStyle() { return this.anchor.style; }
    get panelUp() { return this.anchor.up; }

    #onChange: (value: string) => void = () => { };
    #onTouched: () => void = () => { };

    constructor(
        private host: ElementRef<HTMLElement>,
        private cdr: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
    ) {
        // En el cuerpo y no como inicializador de campo: necesita `host`, que es
        // una propiedad de parámetro.
        this.anchor = new DxcPanelAnchor(
            this.host.nativeElement, '.dxc-combo__field', () => this.cdr.markForCheck());
    }

    ngOnDestroy() { this.anchor.detach(); }

    get selected() { return this.options.find(option => option.value === this.value); }
    get selectedLabel() { return this.selected?.triggerLabel ?? this.optionLabel(this.selected); }

    /**
     * La etiqueta de una opción, siempre como cadena.
     *
     * `DxcSelectOption.label` se declara `string`, pero lo que llega de un
     * catálogo del backend no siempre lo es: en las ubicaciones guardadas
     * `name` es opcional y dos filas llegaban con `null`. Una sola de esas
     * bastaba para tumbar el panel entero — ver `highlight()`.
     */
    optionLabel(option?: DxcSelectOption | null): string {
        return String(option?.label ?? option?.value ?? '');
    }

    /** Lo que se ve en el campo: al escribir, lo tecleado; si no, la selección. */
    get displayText() { return this.typing ? this.query : this.selectedLabel; }

    /** Con algo seleccionado, el hueco lo ocupa la etiqueta; el marcador sobra. */
    get inputPlaceholder() {
        if (this.typing) return this.selectedLabel || this.searchPlaceholder;
        return this.selectedLabel ? '' : this.placeholder;
    }

    /** Lo que se pinta y lo que recorre el teclado: con filtro, las coincidencias. */
    get visibleOptions() {
        const query = this.query.trim().toLowerCase();
        if (!this.typing || !query) return this.options;
        return this.options.filter(option =>
            this.optionLabel(option).toLowerCase().includes(query) ||
            String(option.value ?? '').toLowerCase().includes(query) ||
            String(option.meta ?? '').toLowerCase().includes(query)
        );
    }

    /**
     * Lo que se dibuja de verdad, y cuántas quedan fuera. Va junto porque la
     * plantilla necesita las dos cosas y filtrar 987 opciones tres veces por
     * ciclo es justo lo que hay que evitar: se resuelve una vez con `@let`.
     */
    get optionView(): { list: DxcSelectOption[], hidden: number } {
        const all = this.visibleOptions;
        if (all.length <= this.maxRendered) return { list: all, hidden: 0 };
        return { list: all.slice(0, this.maxRendered), hidden: all.length - this.maxRendered };
    }

    /** Resalta el tramo que coincide, para que se vea por qué está en la lista. */
    highlight(rawLabel: string | null | undefined): SafeHtml {
        // Una etiqueta que no es cadena reventaba en `escape()` —
        // `Cannot read properties of null (reading 'replace')`— y el fallo NO se
        // quedaba en su fila: la excepción aborta la pasada de actualización de
        // la plantilla, así que las 987 etiquetas se quedaban SIN escribir. Eso
        // es el panel en blanco que se veía; las opciones estaban ahí, mudas.
        const label = this.optionLabel({ value: '', label: rawLabel as string });
        const query = this.query.trim();
        const escape = (text: string) => text.replace(/[&<>"']/g, char =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] as string));

        if (!this.typing || !query) return this.sanitizer.bypassSecurityTrustHtml(escape(label));

        const at = label.toLowerCase().indexOf(query.toLowerCase());
        if (at < 0) return this.sanitizer.bypassSecurityTrustHtml(escape(label));

        return this.sanitizer.bypassSecurityTrustHtml(
            escape(label.slice(0, at)) +
            '<mark class="dxc-mark">' + escape(label.slice(at, at + query.length)) + '</mark>' +
            escape(label.slice(at + query.length))
        );
    }

    // --- ControlValueAccessor ------------------------------------------------

    writeValue(value: string) { this.#value = value ?? ''; this.cdr.markForCheck(); }
    registerOnChange(fn: (value: string) => void) { this.#onChange = fn; }
    registerOnTouched(fn: () => void) { this.#onTouched = fn; }
    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
        if (disabled) this.close();
        this.cdr.markForCheck();
    }

    // --- Interacción ---------------------------------------------------------

    onFocus() {
        if (this.disabled || this.open) return;
        this.openPanel();
    }

    /**
     * Un clic sobre el campo ya enfocado lo cierra, como haría un desplegable.
     * Se hace en `mousedown` porque en `click` el foco ya se ha movido y no se
     * distingue «abrir» de «cerrar».
     */
    onFieldDown(event: MouseEvent) {
        if (this.disabled) return;
        if (this.open && document.activeElement === this.fieldRef?.nativeElement) {
            event.preventDefault();
            this.close();
        }
    }

    onType(text: string) {
        this.query = text;
        this.typing = true;
        if (!this.open) this.openPanel();
        this.activeIndex = this.optionView.list.length ? 0 : -1;
        this.cdr.markForCheck();
    }

    toggle() {
        if (this.disabled) return;
        if (this.open) { this.close(); return; }
        this.openPanel();
        this.fieldRef?.nativeElement.focus();
    }

    select(option: DxcSelectOption) {
        if (option.disabled) return;
        this.#value = option.value;
        this.#onChange(option.value);
        this.valueChange.emit(option.value);
        this.close();
    }

    clear() {
        this.#value = '';
        this.#onChange('');
        this.valueChange.emit('');
        this.query = '';
        this.typing = false;
        this.cdr.markForCheck();
    }

    close() {
        if (!this.open) return;
        this.open = false;
        // El texto a medio teclear no se queda en el campo: no es un valor.
        this.query = '';
        this.typing = false;
        this.anchor.detach();
        this.#onTouched();
        this.cdr.markForCheck();
    }

    private openPanel() {
        this.open = true;
        this.query = '';
        this.typing = false;
        // Sobre la lista dibujada y no sobre `options`: con el tope puesto, el
        // índice de la opción 500 de 987 no apunta a nada que exista en el DOM.
        this.activeIndex = Math.max(0, this.optionView.list.findIndex(option => option.value === this.value));
        this.anchor.width = this.panelWidth;
        this.anchor.maxWidth = this.panelMaxWidth;
        this.anchor.attach();
        this.cdr.markForCheck();
    }

    onKeydown(event: KeyboardEvent) {
        switch (event.key) {
            case 'Escape':
                if (!this.open) return;
                this.close();
                // Con el panel abierto, Escape cierra SOLO el panel. Sin esto el
                // evento sigue subiendo y el diálogo de Material que contiene al
                // campo se cierra también: el usuario descarta el formulario
                // entero cuando solo quería cerrar la lista. `preventDefault` no
                // basta — el overlay del CDK no mira `defaultPrevented`.
                event.stopPropagation();
                break;
            case 'Tab':
                this.close();
                return;   // sin preventDefault: el foco sigue su curso
            case 'Enter': {
                const options = this.optionView.list;
                if (this.open && this.activeIndex >= 0 && options[this.activeIndex]) this.select(options[this.activeIndex]);
                else this.toggle();
                break;
            }
            case 'ArrowDown':
            case 'ArrowUp': {
                if (!this.open) { this.openPanel(); break; }
                const delta = event.key === 'ArrowDown' ? 1 : -1;
                this.activeIndex = Math.min(this.optionView.list.length - 1, Math.max(0, this.activeIndex + delta));
                this.#scrollActiveIntoView();
                this.cdr.markForCheck();
                break;
            }
            case 'Backspace':
                // Con el campo vacío, Retroceso quita la selección.
                if (!this.typing && this.value) { this.clear(); break; }
                return;
            default:
                return;
        }
        event.preventDefault();
    }

    // --- Teclado y foco ------------------------------------------------------

    #scrollActiveIntoView() {
        requestAnimationFrame(() => {
            const active = this.host.nativeElement.querySelector('.dxc-combo__option--active');
            active?.scrollIntoView({ block: 'nearest' });
        });
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentDown(event: Event) {
        // mousedown y no click: el manejador de click puede re-renderizar, y para
        // cuando el evento llega a document su target ya no está en el DOM.
        if (this.open && !this.host.nativeElement.contains(event.target as Node)) this.close();
    }

    @HostListener('focusout', ['$event'])
    onFocusOut(event: FocusEvent) {
        const next = event.relatedTarget as Node | null;
        if (this.open && next && !this.host.nativeElement.contains(next)) this.close();
    }
}
